import { SourceMapConsumer } from 'source-map-js'

export interface ResolvedFrame {
  functionName: string | null
  /** Cleaned original source path, e.g. "src/shared/hooks/useAuth.ts" */
  source: string | null
  line: number
  column: number
  /** True when the frame belongs to app code (not React/logger internals) */
  isUserCode: boolean
  /** Original unparsed frame string */
  raw: string
}

// ─── Frame parser ──────────────────────────────────────────────────────────────

interface RawFrame {
  functionName: string | null
  url: string
  line: number
  column: number
  raw: string
}

const FRAME_RE = /^\s+at (?:([\w.<>/[\] ]+) \()?(.+?):(\d+):(\d+)\)?$/
const FIREFOX_FRAME_RE = /^\s*(.*?)@(.+?):(\d+):(\d+)\s*$/

function parseFrames(stack: string): RawFrame[] {
  return stack
    .split('\n')
    .slice(1) // drop "Error" header
    .map(line => {
      const m = line.match(FRAME_RE) ?? line.match(FIREFOX_FRAME_RE)
      if (!m) return null
      return {
        functionName: m[1]?.trim() || null,
        url: m[2],
        line: parseInt(m[3], 10),
        column: parseInt(m[4], 10),
        raw: line.trim()
      }
    })
    .filter((f): f is RawFrame => f !== null)
}

// ─── Source map cache ──────────────────────────────────────────────────────────

const consumerCache = new Map<string, SourceMapConsumer | null>()
const MAX_CONSUMER_CACHE_SIZE = 50

function cacheConsumer(url: string, consumer: SourceMapConsumer | null): void {
  if (consumerCache.size >= MAX_CONSUMER_CACHE_SIZE) {
    const oldest = consumerCache.keys().next().value
    if (oldest) consumerCache.delete(oldest)
  }
  consumerCache.set(url, consumer)
}

async function getConsumer(url: string): Promise<SourceMapConsumer | null> {
  if (consumerCache.has(url)) return consumerCache.get(url) ?? null

  try {
    const baseUrl = url.split('?')[0]
    const res = await fetch(`${baseUrl}.map`)
    if (!res.ok) {
      cacheConsumer(url, null)
      return null
    }
    const raw = await res.json()
    const consumer = new SourceMapConsumer(raw)
    cacheConsumer(url, consumer)
    return consumer
  } catch {
    cacheConsumer(url, null)
    return null
  }
}

// ─── Path cleaner ──────────────────────────────────────────────────────────────

const REACT_SOURCE_RE = /node_modules\/(react|react-dom|scheduler)\//
const LOGGER_SOURCE_RE =
  /services\/logger|LoggerDB|LoggerService|networkInterceptor|localStorageInterceptor|errorInterceptor/
const REACT_URL_RE = /lib-react\.js|react-dom/

function cleanSource(source: string): string {
  // webpack://project-name/./src/... → src/...
  // webpack:///./src/...            → src/...
  // webpack://project-name/src/...  → src/...  (no leading ./)
  return source
    .replace(/^webpack:\/\/[^/]*\/\.\//, '')
    .replace(/^webpack:\/\/\/\.\//, '')
    .replace(/^webpack:\/\/[^/]*\//, '')
    .replace(/\?$/, '')
}

function isUserCode(source: string | null): boolean {
  if (!source) return false
  return !REACT_SOURCE_RE.test(source) && !LOGGER_SOURCE_RE.test(source)
}

// ─── Public API ────────────────────────────────────────────────────────────────

export async function resolveStack(rawStack: string): Promise<ResolvedFrame[]> {
  const frames = parseFrames(rawStack)

  const resolved = await Promise.all(
    frames.map(async (frame): Promise<ResolvedFrame> => {
      // Skip React bundle URLs — no point resolving them
      if (REACT_URL_RE.test(frame.url)) {
        return {
          functionName: frame.functionName,
          source: null,
          line: frame.line,
          column: frame.column,
          isUserCode: false,
          raw: frame.raw
        }
      }

      const consumer = await getConsumer(frame.url)

      if (!consumer) {
        return {
          functionName: frame.functionName,
          source: frame.url,
          line: frame.line,
          column: frame.column,
          isUserCode: false,
          raw: frame.raw
        }
      }

      const pos = consumer.originalPositionFor({
        line: frame.line,
        column: frame.column
      })
      const source = pos.source ? cleanSource(pos.source) : null

      return {
        functionName: pos.name || frame.functionName,
        source,
        line: pos.line ?? frame.line,
        column: pos.column ?? frame.column,
        isUserCode: isUserCode(source),
        raw: frame.raw
      }
    })
  )

  return resolved
}
